import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  Fade,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Compress,
  Expand,
  AutoFixHigh,
  Close as CloseIcon,
  Person,
  Groups,
  Refresh,
} from "@mui/icons-material";

export type OperationType =
  | "condense"
  | "expatiate"
  | "toFirstPerson"
  | "toThirdPerson"
  | "addContext"
  | null;

interface NarrativeConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  operationType: OperationType;
}

export const NarrativeConfirmationModal: React.FC<
  NarrativeConfirmationModalProps
> = ({ open, onClose, onConfirm, operationType }) => {
  const theme = useTheme();
  const getConfirmationDetails = (operation: OperationType) => {
    switch (operation) {
      case "condense":
        return {
          title: "Condense Narrative",
          description:
            "AI will rewrite this narrative to be more concise while preserving all important details.",
        };
      case "expatiate":
        return {
          title: "Expand Narrative",
          description:
            "AI will rewrite this narrative to be more detailed and comprehensive.",
        };
      case "toFirstPerson":
        return {
          title: "Convert to 1st Person",
          description:
            'AI will rewrite this narrative from a first-person perspective, using "I" and "my" throughout.',
        };
      case "toThirdPerson":
        return {
          title: "Convert to 3rd Person",
          description:
            "AI will rewrite this narrative from a third-person perspective, referring to the officer by name.",
        };
      case "addContext":
        return {
          title: "Regenerate with Context",
          description:
            "AI will regenerate this narrative incorporating the additional context you provided.",
        };
      default:
        return {
          title: "Regenerate Narrative",
          description: "AI will regenerate this narrative.",
        };
    }
  };

  const details = getConfirmationDetails(operationType);

  const triggerHapticFeedback = () => {
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const handleConfirmWithHaptic = () => {
    triggerHapticFeedback();
    onConfirm();
  };

  const getIcon = () => {
    switch (operationType) {
      case "condense":
        return <Compress sx={{ fontSize: 32, color: "white" }} />;
      case "expatiate":
        return <Expand sx={{ fontSize: 32, color: "white" }} />;
      case "toFirstPerson":
        return <Person sx={{ fontSize: 32, color: "white" }} />;
      case "toThirdPerson":
        return <Groups sx={{ fontSize: 32, color: "white" }} />;
      case "addContext":
        return <Refresh sx={{ fontSize: 32, color: "white" }} />;
      default:
        return <AutoFixHigh sx={{ fontSize: 32, color: "white" }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="narrative-confirmation-dialog-title"
      aria-describedby="narrative-confirmation-dialog-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          minWidth: 380,
          maxWidth: 420,
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          {getIcon()}
        </Box>

        <Typography
          id="narrative-confirmation-dialog-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
        >
          {details.title}
        </Typography>

        <Typography
          id="narrative-confirmation-dialog-description"
          variant="body1"
          sx={{ color: "text.secondary", lineHeight: 1.6 }}
        >
          {details.description}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          gap: 1.5,
          flexDirection: "column",
          alignItems: "stretch",
          "& > :not(:first-of-type)": {
            marginLeft: 0,
          },
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disableElevation
          onClick={handleConfirmWithHaptic}
          startIcon={
            operationType === "condense" ? (
              <Compress />
            ) : operationType === "expatiate" ? (
              <Expand />
            ) : operationType === "toFirstPerson" ? (
              <Person />
            ) : operationType === "toThirdPerson" ? (
              <Groups />
            ) : operationType === "addContext" ? (
              <Refresh />
            ) : undefined
          }
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "primary.dark",
              transform: "translateY(-2px)",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
          }}
        >
          {operationType === "condense"
            ? "Condense"
            : operationType === "expatiate"
              ? "Expand"
              : operationType === "toFirstPerson"
                ? "Convert to 1st Person"
                : operationType === "toThirdPerson"
                  ? "Convert to 3rd Person"
                  : operationType === "addContext"
                    ? "Regenerate"
                    : "Confirm"}
        </Button>
        <Button
          onClick={onClose}
          color="inherit"
          variant="text"
          fullWidth
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
